import { useState, useMemo } from 'react';
import type { SortingState, PaginationState } from '@hs/ui';
import { Button } from '@heroui/react';
import { IconRefresh as RefreshCw } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { PageDataGrid, PageDataGridActionToolbar } from '@/components/layouts/page/PageDataGrid';

type RelatedResource = {
  id?: string;
  name?: string | null;
  code?: string | null;
};

type EntitlementGrant = {
  id: string;
  resourceDefinitionId?: string;
  resourceDefinition?: RelatedResource | null;
  resourceModeSnapshot?: string;
  grantMode?: string;
  sourceType?: string;
  quantityGranted?: number;
  validFrom?: string;
  validTo?: string | null;
  status?: string;
  createdAt?: string;
};

const GRANT_SORT_FIELDS = new Set([
  'status',
  'sourceType',
  'validFrom',
  'validTo',
  'createdAt',
  'updatedAt',
]);

function resourceDefinitionName(row: { resourceDefinition?: RelatedResource | null }) {
  const name = row.resourceDefinition?.name?.trim();
  return name || '—';
}

export function EntitlementGrantListPanel() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [enumFilters, setEnumFilters] = useState<Record<string, string>>({});

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const sortItem = sorting[0];
  const sortBy =
    sortItem && GRANT_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as
          | 'status'
          | 'sourceType'
          | 'validFrom'
          | 'validTo'
          | 'createdAt'
          | 'updatedAt')
      : undefined;

  const { data, isLoading, refetch } = trpc.bizEntitlement.grant.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortBy
        ? { sortBy, sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
      ...(enumFilters.status
        ? {
            status: enumFilters.status as 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REVOKED',
          }
        : {}),
      ...(enumFilters.grantMode
        ? { grantMode: enumFilters.grantMode as 'PER_PERIOD' | 'CONTINUOUS' }
        : {}),
      ...(enumFilters.sourceType
        ? {
            sourceType: enumFilters.sourceType as
              | 'SUBSCRIPTION'
              | 'ADDON'
              | 'PROMOTION'
              | 'MANUAL_ADJUSTMENT'
              | 'REFUND',
          }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const rows = (data?.items ?? []) as EntitlementGrant[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const columnOverrides = useMemo(
    () => ({
      resourceDefinitionId: {
        header: '资源定义',
        allowsSorting: false,
        cell: (row: EntitlementGrant) => resourceDefinitionName(row),
      },
    }),
    []
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <PageDataGrid<EntitlementGrant>
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        source="entitlement_grant"
        data={rows}
        recordCount={total}
        pageCount={pageCount}
        isLoading={isLoading}
        emptyMessage="暂无数据"
        getRowId={(row) => row.id}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        enumFilters={enumFilters}
        onEnumFiltersChange={setEnumFilters}
        columnOverrides={columnOverrides}
        metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
        paginationBar={{}}
      >
        <PageDataGridActionToolbar>
          <Button variant="secondary" onPress={() => void refetch()}>
            <RefreshCw className="size-4" stroke={1.5} />
            刷新
          </Button>
        </PageDataGridActionToolbar>
      </PageDataGrid>
    </div>
  );
}
